import { useMutation } from "react-query";

import { HttpStatusCode } from "axios";

import { UploadExcelResult, uploadCustomColumnFile } from "api/data";

import { EntityKind } from "models/entityKind";

import {
  convertToUserFriendlyMessage,
  extractErrorMessage
} from "../hooks/useErrorMessages";

export function useUploadCustomColumnFileMutation(
  keyHeaderRef: React.MutableRefObject<string>,
  entityKind: EntityKind,
  onSuccess: (data: UploadExcelResult) => void
) {
  return useMutation(
    async (file) => await uploadCustomColumnFile(file, keyHeaderRef.current, entityKind),
    {
      onSuccess: (data) => {
        if (!data) return;
        // Pass the response through when there is any importable column OR any headerless
        // column the user can still name. Only a genuinely empty response (no columns and
        // nothing to name) is swallowed. An all-headerless file (columns: [] with a
        // non-empty unnamedColumns) must reach the wizard so the naming step opens —
        // otherwise the naming step never shows and Next dereferences a null dataRef.
        const hasNothingToShow =
          data.columns.length === 0 && (data.unnamedColumns?.length ?? 0) === 0;
        if (hasNothingToShow) return;
        onSuccess(data);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (err: any) => {
        if (err?.response?.status === HttpStatusCode.PayloadTooLarge) {
          throw new Error(
            "The file exceeds the 100 MB upload limit. Please reduce the file size and try again."
          );
        }
        if (!err?.response?.data) {
          throw new Error("Unable to connect to backend service.");
        }
        const errorMessage = extractErrorMessage(err.response.data);
        const userFriendlyMessage = convertToUserFriendlyMessage(errorMessage);
        throw new Error(userFriendlyMessage);
      }
    }
  );
}
